import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { calculatePercentagePosition, getBlockColor, type BlockData } from '../config/wordOverlayConfig';
import WordOverlay from './WordOverlay';
import BlockToolbar from './BlockToolbar';
import WordToolbar from './WordToolbar';
import { useWordDataContext } from '../contexts/WordDataContext';
import type { WordData } from '../config/wordOverlayConfig';

interface BlockOverlayProps {
  block: BlockData;
  blockIndex: number;
  pageIndex: number;
  imageDimensions: { width: number; height: number };
  highlightedWords: Set<string>;
  onWordClick: (word: WordData, block: BlockData) => void;
}

const BlockOverlay: React.FC<BlockOverlayProps> = ({
  block,
  blockIndex,
  pageIndex,
  imageDimensions,
  highlightedWords,
  onWordClick
}) => {
  const { state, actions } = useWordDataContext();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const blockPosition = calculatePercentagePosition(block.boundingBox, imageDimensions);
  const blockColor = getBlockColor(block.readBlock);
  const blockId: number = typeof block.blockId === 'number' 
    ? block.blockId 
    : (isNaN(Number(block.blockId)) ? 0 : Number(block.blockId));

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // If overlays are hidden, turn them on first
    const overlayContainer = (e.currentTarget as HTMLElement).closest('.word-overlay-container');
    if (overlayContainer?.classList.contains('hide-overlays')) {
      // Remove hide-overlays class from all containers
      const allContainers = document.querySelectorAll('.word-overlay-container');
      allContainers.forEach(container => {
        container.classList.remove('hide-overlays');
      });
      // Trigger the overlay toggle in SpreadEditor by dispatching a custom event
      window.dispatchEvent(new CustomEvent('enableOverlays'));
    }
    
    if (state.editMode === 'delete-block') {
      setShowDeleteConfirm(true);
    }
  };

  const handleConfirmDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    console.log('[BlockOverlay] Deleting block:', { pageIndex, blockId });
    actions.deleteBlock(pageIndex, blockId);
    setShowDeleteConfirm(false);
    // Don't clear edit mode - allow deleting multiple blocks
  };

  const handleCancelDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteConfirm(false);
  };

  // Determine cursor style based on edit mode
  const getCursorStyle = () => {
    if (state.editMode === 'delete-block') return 'pointer';
    return 'default';
  };

  return (
    <>
      <div
        className="overlay-block"
        data-block-id={String(block.blockId)}
        data-block-type={block.blockType}
        data-block-index={String(blockIndex)}
        style={{
          '--block-left': `${blockPosition.left}%`,
          '--block-top': `${blockPosition.top}%`,
          '--block-width': `${blockPosition.width}%`,
          '--block-height': `${blockPosition.height}%`,
          '--block-color': blockColor,
          cursor: getCursorStyle(),
        } as React.CSSProperties}
        onClick={handleClick}
      >
        {/* Modern Toolbar */}
        <BlockToolbar block={block} pageIndex={pageIndex} />
        
        {/* Word Toolbar - appears below block toolbar when word is selected */}
        <WordToolbar block={block} pageIndex={pageIndex} />

        {block.words && block.words.length > 0 && (
          <>
            {block.words.map((word: WordData, wordIndex: number) => (
              <WordOverlay
                key={word.wordId}
                word={word}
                block={block}
                pageIndex={pageIndex}
                imageDimensions={imageDimensions}
                isHighlighted={highlightedWords.has(String(word.wordId))}
                onClick={onWordClick}
              />
            ))}
          </>
        )}
      </div>
      
      {/* Delete Confirmation Dialog - rendered in portal */}
      {showDeleteConfirm && createPortal(
        <>
          {/* Backdrop */}
          <div
            onClick={(e) => {
              e.stopPropagation();
              handleCancelDelete(e);
            }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 99999,
            }}
          />
          {/* Dialog */}
          <div
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'var(--bg-primary)',
              border: '1px solid var(--border-primary)',
              borderRadius: '8px',
              padding: '20px',
              zIndex: 100000,
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            }}
          >
            <h3 style={{ margin: '0 0 12px 0', fontSize: '16px' }}>Delete Block?</h3>
            <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: 'var(--text-secondary)' }}>
              This will delete the block and all its words. This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={handleCancelDelete}
                onMouseDown={(e) => e.stopPropagation()}
                style={{
                  padding: '6px 12px',
                  border: '1px solid var(--border-primary)',
                  borderRadius: '4px',
                  backgroundColor: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                onMouseDown={(e) => e.stopPropagation()}
                style={{
                  padding: '6px 12px',
                  border: '1px solid var(--accent-primary)',
                  borderRadius: '4px',
                  backgroundColor: 'var(--accent-primary)',
                  color: 'white',
                  cursor: 'pointer',
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
};

export default BlockOverlay;
